import { useCreateConversationMutation } from "#features/conversations/api/useCreateConversationMutation.ts";
import { useCreateConversationErrorHandler } from "#features/conversations/hooks/useCreateConversationErrorHandler.tsx";
import { useTracking } from "#hooks/useTracking.ts";
import { MixpanelEvent } from "#types/tracking.ts";
import { useCallback, useRef, useState } from "react";

export const useGetOrCreateConversationId = (existingConversationId?: string) => {
    const [draftConversationId, setDraftConversationId] = useState<string | undefined>(undefined);
    const inFlightRef = useRef<Promise<string | undefined> | null>(null);
    const { mutateAsync } = useCreateConversationMutation();
    const handleCreateConversationError = useCreateConversationErrorHandler();
    const { trackEvent } = useTracking();

    const getOrCreateConversationId = useCallback(async (): Promise<string | undefined> => {
        if (existingConversationId) {
            return existingConversationId;
        }

        if (draftConversationId) {
            return draftConversationId;
        }

        // Reuse an in-flight create so concurrent callers don't each create a conversation.
        if (inFlightRef.current) {
            return inFlightRef.current;
        }

        const promise = (async () => {
            try {
                const { conversation } = await mutateAsync();
                const { conversationId } = conversation;

                trackEvent(MixpanelEvent.NewConversationStarted, {
                    "Conversation Id": conversationId
                });

                setDraftConversationId(conversationId);

                return conversationId;
            } catch (error) {
                handleCreateConversationError(error);

                // Clear only on failure so a retry can start a fresh create. On success we keep
                // the resolved promise cached: a late caller in the same render frame (before
                // draftConversationId is observable) still reuses it instead of creating a second
                // conversation.
                inFlightRef.current = null;

                return undefined;
            }
        })();

        inFlightRef.current = promise;

        return promise;
    }, [existingConversationId, draftConversationId, mutateAsync, trackEvent, handleCreateConversationError]);

    const conversationId = existingConversationId ?? draftConversationId;

    return {
        conversationId,
        getOrCreateConversationId
    };
};
